import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    Chip,
    Collapse,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    InputLabel,
    Link,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Select,
    SelectChangeEvent,
    Skeleton,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useAlert } from '../core/AlertContext';
import PageHeader from '../core/PageHeader';
import { checkPermission, getCurrentUserId } from '../utils/permission';
import {
    IFileItem,
    IStorageGroup,
    IFolderTreeNode,
    IFolderUpsertInput,
    folderUpsertSchema,
} from '../Interfaces/IStorage';
import {
    useCreateFolderMutation,
    useDeleteFileMutation,
    useDeleteFolderMutation,
    downloadStorageFile,
    useFolderFilesQuery,
    useStorageGroupsQuery,
    useRenameFileMutation,
    useStorageTreeQuery,
    useToggleFileFavoriteMutation,
    useToggleFolderFavoriteMutation,
    useUpdateFolderMutation,
    useUploadFileMutation,
} from '../hooks/storage';
import { fieldSx, primaryButtonSx, secondaryButtonSx, dangerButtonSx, STATUS_TONES } from '../core/pvtaStyles';
import {
    Audience,
    audienceFromMode,
    describeAudience,
    directAudienceFromNode,
    effectiveAudienceFromNode,
    intersectAudience,
    isRestrictedByParent,
} from './visibility';

type FolderDialogMode = 'create' | 'edit';
type MobilePane = 'folders' | 'files';

type FolderDialogState = {
    name: string;
    description: string;
    visibility_mode: 'DEFAULT_INTERNAL' | 'ALL_USERS' | 'RESTRICTED';
    visible_groups: number[];
    upload_mode: 'DEFAULT_INTERNAL' | 'ALL_USERS' | 'RESTRICTED';
    upload_groups: number[];
};

type FolderDialogErrors = Partial<Record<keyof FolderDialogState, string>>;

const getGroupLabel = (group: IStorageGroup): string => group.customGroup ?? group.name ?? group.title ?? 'Unbekannt';
const isInternalRestrictedGroup = (groupId: number): boolean => [1, 2, 3].includes(groupId);

const normalizeMode = (
    mode: string | undefined,
    groups: number[] | undefined,
    includeGuestsFlag: boolean | undefined
): 'DEFAULT_INTERNAL' | 'ALL_USERS' | 'RESTRICTED' => {
    if (mode === 'ALL_USERS' || includeGuestsFlag) {
        return 'ALL_USERS';
    }
    if (mode === 'RESTRICTED' || mode === 'GROUPS_ONLY' || (groups?.length ?? 0) > 0) {
        return 'RESTRICTED';
    }
    return 'DEFAULT_INTERNAL';
};

const mapUiModeToApiMode = (mode: FolderDialogState['visibility_mode']): 'ALL_AUTHENTICATED_NON_GUESTS' | 'GROUPS_ONLY' =>
    mode === 'RESTRICTED' ? 'GROUPS_ONLY' : 'ALL_AUTHENTICATED_NON_GUESTS';

const formatDateTime = (value: string | null | undefined): string => {
    if (!value) {
        return '-';
    }
    return new Date(value).toLocaleString('de-DE');
};

const formatBytes = (size: number): string => {
    if (size < 1024) {
        return `${size} B`;
    }
    const units = ['KB', 'MB', 'GB', 'TB'];
    let value = size / 1024;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024;
        unitIndex += 1;
    }
    return `${value.toFixed(1)} ${units[unitIndex]}`;
};

const parseSelectNumberArray = (event: SelectChangeEvent<number[]>): number[] => {
    const rawValue = event.target.value;
    if (Array.isArray(rawValue)) {
        return rawValue.map((value) => Number(value));
    }
    return rawValue.split(',').map((value) => Number(value));
};

const findFolderById = (nodes: IFolderTreeNode[], id: number): IFolderTreeNode | null => {
    for (const node of nodes) {
        if (node.id === id) {
            return node;
        }
        const childMatch = findFolderById(node.children, id);
        if (childMatch) {
            return childMatch;
        }
    }
    return null;
};

const buildFolderPath = (nodes: IFolderTreeNode[], id: number): IFolderTreeNode[] => {
    const walk = (list: IFolderTreeNode[], targetId: number, currentPath: IFolderTreeNode[]): IFolderTreeNode[] | null => {
        for (const node of list) {
            const nextPath = [...currentPath, node];
            if (node.id === targetId) {
                return nextPath;
            }
            const childResult = walk(node.children, targetId, nextPath);
            if (childResult) {
                return childResult;
            }
        }
        return null;
    };

    return walk(nodes, id, []) ?? [];
};

const hiddenFolderNames = new Set(['Event Media', 'Event-Media', 'Blog Media', 'Blog-Media']);

const filterEventMediaFolder = (nodes: IFolderTreeNode[]): IFolderTreeNode[] => (
    nodes
        .filter((node) => !hiddenFolderNames.has(node.name))
        .map((node) => ({
            ...node,
            children: filterEventMediaFolder(node.children),
        }))
);

const filterFavoriteFolders = (nodes: IFolderTreeNode[]): IFolderTreeNode[] => (
    nodes
        .map((node) => {
            const children = filterFavoriteFolders(node.children);
            if (!node.is_favorite && children.length === 0) {
                return null;
            }
            return {
                ...node,
                children,
            };
        })
        .filter((node): node is IFolderTreeNode => node !== null)
);

const FolderDialog: React.FC<{
    open: boolean;
    mode: FolderDialogMode;
    initialValues: FolderDialogState;
    groupOptions: IStorageGroup[];
    parentEffective: Audience | null;
    onClose: () => void;
    onSubmit: (payload: IFolderUpsertInput) => Promise<void>;
}> = ({ open, mode, initialValues, groupOptions, parentEffective, onClose, onSubmit }) => {
    const [state, setState] = useState<FolderDialogState>(initialValues);
    const [errors, setErrors] = useState<FolderDialogErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const restrictedGroupOptions = useMemo(
        () => groupOptions.filter((group) => isInternalRestrictedGroup(group.id)),
        [groupOptions]
    );
    const resolveGroupName = useCallback(
        (groupId: number): string => getGroupLabel(groupOptions.find((option) => option.id === groupId) ?? { id: groupId }),
        [groupOptions]
    );
    // Vorschau „Wer kann das sehen?": direkte Wahl, eingeschränkt durch die
    // effektive Sichtbarkeit des Elternordners (Vererbung, wie im Backend).
    const directAudience = useMemo(
        () => audienceFromMode(state.visibility_mode, state.visible_groups),
        [state.visibility_mode, state.visible_groups]
    );
    const previewAudience = useMemo(
        () => (parentEffective ? intersectAudience(parentEffective, directAudience) : directAudience),
        [parentEffective, directAudience]
    );
    const limitedByParent = isRestrictedByParent(directAudience, parentEffective);

    useEffect(() => {
        if (!open) {
            return;
        }
        setState(initialValues);
        setErrors({});
    }, [initialValues, open]);

    const validateAndSubmit = async () => {
        const parsed = folderUpsertSchema.safeParse(state);
        if (!parsed.success) {
            const nextErrors: FolderDialogErrors = {};
            parsed.error.issues.forEach((issue) => {
                const field = issue.path[0] as keyof FolderDialogState | undefined;
                if (field && !nextErrors[field]) {
                    nextErrors[field] = issue.message;
                }
            });
            setErrors(nextErrors);
            return;
        }

        setIsSubmitting(true);
        try {
            await onSubmit(parsed.data);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 700 }}>
                {mode === 'create' ? 'Neuer Ordner' : 'Ordner bearbeiten'}
            </DialogTitle>
            <DialogContent sx={{ pt: 4, overflow: 'visible' }}>
                <Stack spacing={2} sx={{ pt: 0.5 }}>
                    <TextField
                        label="Name"
                        value={state.name}
                        onChange={(event) => setState((prev) => ({ ...prev, name: event.target.value }))}
                        error={Boolean(errors.name)}
                        helperText={errors.name}
                        required
                        sx={fieldSx}
                    />
                    <TextField
                        label="Beschreibung"
                        value={state.description}
                        onChange={(event) => setState((prev) => ({ ...prev, description: event.target.value }))}
                        multiline
                        minRows={2}
                        sx={fieldSx}
                    />

                    <FormControl fullWidth>
                        <InputLabel id="storage-visibility-mode-label">Sichtbarkeit</InputLabel>
                        <Select
                            labelId="storage-visibility-mode-label"
                            label="Sichtbarkeit"
                            value={state.visibility_mode}
                            onChange={(event) =>
                                setState((prev) => ({ ...prev, visibility_mode: event.target.value as FolderDialogState['visibility_mode'] }))
                            }
                        >
                            <MenuItem value="DEFAULT_INTERNAL">Intern (alle außer Gäste)</MenuItem>
                            <MenuItem value="ALL_USERS">Alle (inkl. Gäste)</MenuItem>
                            <MenuItem value="RESTRICTED">Eingeschränkt (nur bestimmte interne Gruppen)</MenuItem>
                        </Select>
                    </FormControl>
                    {state.visibility_mode === 'RESTRICTED' && (
                        <FormControl error={Boolean(errors.visible_groups)}>
                            <InputLabel id="storage-visible-groups-label">Darf sehen:</InputLabel>
                            <Select
                                labelId="storage-visible-groups-label"
                                multiple
                                value={state.visible_groups}
                                onChange={(event) =>
                                    setState((prev) => ({ ...prev, visible_groups: parseSelectNumberArray(event) }))
                                }
                                label="Darf sehen:"
                                renderValue={(selected) =>
                                    selected
                                        .map((groupId) => getGroupLabel(groupOptions.find((option) => option.id === groupId) ?? { id: groupId }))
                                        .join(', ')
                                }
                            >
                                {restrictedGroupOptions.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                        <Checkbox checked={state.visible_groups.includes(option.id)} />
                                        <ListItemText primary={getGroupLabel(option)} />
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>{errors.visible_groups}</FormHelperText>
                        </FormControl>
                    )}

                    <Box
                        sx={{
                            borderRadius: 'var(--pvta-radius-sm)',
                            border: '1px solid var(--pvta-border)',
                            backgroundColor: 'var(--pvta-surface-muted)',
                            p: 1.5,
                        }}
                    >
                        <Typography variant="caption" sx={{ color: 'var(--pvta-muted)', fontWeight: 700, display: 'block' }}>
                            Wer kann das sehen?
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }}>
                            {describeAudience(previewAudience, resolveGroupName)}
                        </Typography>
                    </Box>

                    {limitedByParent && (
                        <Alert
                            severity="warning"
                            sx={{
                                borderRadius: 'var(--pvta-radius-sm)',
                                backgroundColor: STATUS_TONES.warning.bg,
                                color: STATUS_TONES.warning.color,
                            }}
                        >
                            Dieser Unterordner kann nicht sichtbarer sein als sein übergeordneter Ordner.
                            Die effektive Sichtbarkeit wird durch den Elternordner eingeschränkt.
                        </Alert>
                    )}

                    <FormControl fullWidth>
                        <InputLabel id="storage-upload-mode-label">Upload</InputLabel>
                        <Select
                            labelId="storage-upload-mode-label"
                            label="Upload"
                            value={state.upload_mode}
                            onChange={(event) => {
                                const nextMode = event.target.value as FolderDialogState['upload_mode'];
                                setState((prev) => {
                                    if (nextMode === 'RESTRICTED') {
                                        return {
                                            ...prev,
                                            upload_mode: nextMode,
                                            upload_groups: prev.upload_groups.length > 0 ? prev.upload_groups : [1],
                                        };
                                    }
                                    return {
                                        ...prev,
                                        upload_mode: nextMode,
                                        upload_groups: [],
                                    };
                                });
                            }}
                        >
                            <MenuItem value="DEFAULT_INTERNAL">Intern (alle außer Gäste)</MenuItem>
                            <MenuItem value="ALL_USERS">Alle (inkl. Gäste)</MenuItem>
                            <MenuItem value="RESTRICTED">Eingeschränkt (nur bestimmte interne Gruppen)</MenuItem>
                        </Select>
                    </FormControl>

                    {state.upload_mode === 'RESTRICTED' && (
                        <FormControl error={Boolean(errors.upload_groups)}>
                            <InputLabel id="storage-upload-groups-label">Darf hochladen:</InputLabel>
                            <Select
                                labelId="storage-upload-groups-label"
                                multiple
                                value={state.upload_groups}
                                onChange={(event) =>
                                    setState((prev) => ({ ...prev, upload_groups: parseSelectNumberArray(event) }))
                                }
                                label="Darf hochladen:"
                                renderValue={(selected) =>
                                    selected
                                        .map((groupId) => getGroupLabel(groupOptions.find((option) => option.id === groupId) ?? { id: groupId }))
                                        .join(', ')
                                }
                            >
                                {restrictedGroupOptions.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                        <Checkbox checked={state.upload_groups.includes(option.id)} />
                                        <ListItemText primary={getGroupLabel(option)} />
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>{errors.upload_groups}</FormHelperText>
                        </FormControl>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button sx={secondaryButtonSx} onClick={onClose}>Abbrechen</Button>
                <Button sx={{ ...primaryButtonSx }} onClick={validateAndSubmit} disabled={isSubmitting}>
                    Speichern
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const StoragePage: React.FC = () => {
    const { showAlert } = useAlert();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
    const [expandedFolderIds, setExpandedFolderIds] = useState<number[]>([]);
    const [fileSearchTerm, setFileSearchTerm] = useState('');
    const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
    const [downloadingFileIds, setDownloadingFileIds] = useState<number[]>([]);

    const [folderDialogOpen, setFolderDialogOpen] = useState(false);
    const [folderDialogMode, setFolderDialogMode] = useState<FolderDialogMode>('create');
    const [createParentId, setCreateParentId] = useState<number | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [fileDeleteCandidate, setFileDeleteCandidate] = useState<IFileItem | null>(null);
    const [renameCandidate, setRenameCandidate] = useState<IFileItem | null>(null);
    const [renameValue, setRenameValue] = useState('');
    const [mobilePane, setMobilePane] = useState<MobilePane>('folders');
    const [mobileFolderActionsAnchorEl, setMobileFolderActionsAnchorEl] = useState<null | HTMLElement>(null);
    const [mobileFileActionsAnchorEl, setMobileFileActionsAnchorEl] = useState<null | HTMLElement>(null);
    const [mobileFileActionsTarget, setMobileFileActionsTarget] = useState<IFileItem | null>(null);

    const { data: tree, isLoading: isLoadingTree } = useStorageTreeQuery();
    const { data: groups } = useStorageGroupsQuery();
    const { data: files, isLoading: isLoadingFiles } = useFolderFilesQuery(selectedFolderId);

    const createFolderMutation = useCreateFolderMutation();
    const updateFolderMutation = useUpdateFolderMutation();
    const deleteFolderMutation = useDeleteFolderMutation();
    const uploadFileMutation = useUploadFileMutation();
    const deleteFileMutation = useDeleteFileMutation();
    const renameFileMutation = useRenameFileMutation();
    const toggleFolderFavoriteMutation = useToggleFolderFavoriteMutation();
    const toggleFileFavoriteMutation = useToggleFileFavoriteMutation();

    const visibleTree = useMemo(() => {
        const filteredTree = filterEventMediaFolder(tree);
        return showOnlyFavorites ? filterFavoriteFolders(filteredTree) : filteredTree;
    }, [showOnlyFavorites, tree]);

    const selectedFolder = useMemo(
        () => (selectedFolderId === null ? null : findFolderById(visibleTree, selectedFolderId)),
        [selectedFolderId, visibleTree]
    );

    const selectedPath = useMemo(
        () => (selectedFolderId === null ? [] : buildFolderPath(visibleTree, selectedFolderId)),
        [selectedFolderId, visibleTree]
    );
    // Effektive Sichtbarkeit des Elternordners – Basis für Dialog-Vorschau/Warnung.
    const dialogParentEffective = useMemo<Audience | null>(() => {
        const parentId = folderDialogMode === 'create' ? createParentId : (selectedFolder?.parent_id ?? null);
        if (parentId === null || parentId === undefined) {
            return null;
        }
        const parentNode = findFolderById(tree ?? [], parentId);
        return parentNode ? effectiveAudienceFromNode(parentNode) : null;
    }, [folderDialogMode, createParentId, selectedFolder, tree]);
    const resolveGroupName = useCallback(
        (groupId: number): string => getGroupLabel(groups.find((option) => option.id === groupId) ?? { id: groupId }),
        [groups]
    );
    const currentUserId = getCurrentUserId();
    const internalRestrictedGroupIds = useMemo(() => {
        const ids = groups.filter((group) => isInternalRestrictedGroup(group.id)).map((group) => group.id);
        return ids.length > 0 ? ids : [1, 2, 3];
    }, [groups]);
    const canManageByGroupFallback = checkPermission(1, 2, 3);
    const canManageSelectedFolder = selectedFolder
        ? (typeof selectedFolder.can_manage === 'boolean' ? selectedFolder.can_manage : canManageByGroupFallback)
        : canManageByGroupFallback;
    const canUploadToSelectedFolder = selectedFolder
        ? (typeof selectedFolder.can_upload === 'boolean' ? selectedFolder.can_upload : canManageByGroupFallback)
        : false;
    const hasMobileFolderActions = Boolean(
        selectedFolder && (canManageSelectedFolder || canUploadToSelectedFolder)
    );

    const handleToggleFolderFavorite = useCallback(async (folder: IFolderTreeNode, isFavorite: boolean) => {
        await toggleFolderFavoriteMutation.mutateAsync({ folderId: folder.id, isFavorite });
    }, [toggleFolderFavoriteMutation]);

    const handleToggleFileFavorite = useCallback(async (file: IFileItem, isFavorite: boolean) => {
        if (!selectedFolder) {
            return;
        }
        await toggleFileFavoriteMutation.mutateAsync({ folderId: selectedFolder.id, fileId: file.id, isFavorite });
    }, [selectedFolder, toggleFileFavoriteMutation]);

    useEffect(() => {
        if (visibleTree.length === 0) {
            setSelectedFolderId(null);
            return;
        }

        if (selectedFolderId === null) {
            setSelectedFolderId(visibleTree[0].id);
            return;
        }

        const exists = findFolderById(visibleTree, selectedFolderId);
        if (!exists) {
            setSelectedFolderId(visibleTree[0].id);
        }
    }, [selectedFolderId, visibleTree]);

    useEffect(() => {
        if (!isMobile) {
            setMobilePane('files');
        }
    }, [isMobile]);

    const handleSelectFolder = useCallback((folderId: number) => {
        setSelectedFolderId(folderId);
        if (isMobile) {
            setMobilePane('files');
        }
    }, [isMobile]);

    const toggleExpand = (folderId: number) => {
        setExpandedFolderIds((prev) =>
            prev.includes(folderId) ? prev.filter((id) => id !== folderId) : [...prev, folderId]
        );
    };

    const openCreateDialog = (parentId: number | null) => {
        setFolderDialogMode('create');
        setCreateParentId(parentId);
        setFolderDialogOpen(true);
    };

    const openEditDialog = () => {
        setFolderDialogMode('edit');
        setFolderDialogOpen(true);
    };

    const getDialogInitialValues = (): FolderDialogState => {
        if (folderDialogMode === 'edit' && selectedFolder) {
            const sourceVisibilityGroups = selectedFolder.visibility_groups ?? selectedFolder.visible_groups ?? [];
            const selectedVisibleGroups = sourceVisibilityGroups.filter((groupId) => isInternalRestrictedGroup(groupId));
            const selectedUploadGroups = (selectedFolder.upload_groups ?? []).filter((groupId) => isInternalRestrictedGroup(groupId));
            return {
                name: selectedFolder.name,
                description: selectedFolder.description ?? '',
                visibility_mode: normalizeMode(selectedFolder.visibility_mode, selectedFolder.visible_groups, selectedFolder.include_guests),
                visible_groups: selectedVisibleGroups.length > 0 ? selectedVisibleGroups : internalRestrictedGroupIds,
                upload_mode: normalizeMode(selectedFolder.upload_mode, selectedFolder.upload_groups, selectedFolder.upload_include_guests),
                upload_groups: selectedUploadGroups.length > 0 ? selectedUploadGroups : internalRestrictedGroupIds,
            };
        }

        return {
            name: '',
            description: '',
            visibility_mode: 'DEFAULT_INTERNAL',
            visible_groups: internalRestrictedGroupIds,
            upload_mode: 'DEFAULT_INTERNAL',
            upload_groups: internalRestrictedGroupIds,
        };
    };

    const handleFolderDialogSubmit = async (payload: IFolderUpsertInput) => {
        const normalizedVisibleGroups = payload.visible_groups.filter((groupId) => isInternalRestrictedGroup(groupId));
        const normalizedUploadGroups = payload.upload_groups.filter((groupId) => isInternalRestrictedGroup(groupId));
        const visibilityModeUi = payload.visibility_mode as FolderDialogState['visibility_mode'];
        const uploadModeUi = payload.upload_mode as FolderDialogState['upload_mode'];
        const payloadForApi: IFolderUpsertInput = {
            ...payload,
            visibility_mode: mapUiModeToApiMode(visibilityModeUi),
            include_guests: visibilityModeUi === 'ALL_USERS',
            visible_groups: visibilityModeUi === 'RESTRICTED' ? normalizedVisibleGroups : [],
            visibility_groups: visibilityModeUi === 'RESTRICTED' ? normalizedVisibleGroups : [],
            upload_mode: mapUiModeToApiMode(uploadModeUi),
            upload_include_guests: uploadModeUi === 'ALL_USERS',
            upload_groups: uploadModeUi === 'RESTRICTED' ? normalizedUploadGroups : [],
            upload_any_authenticated: uploadModeUi === 'RESTRICTED' ? false : undefined,
        };

        try {
            if (folderDialogMode === 'create') {
                const created = await createFolderMutation.mutateAsync({
                    ...payloadForApi,
                    parent_id: createParentId,
                    description: payload.description || undefined,
                });
                setSelectedFolderId(created.id);
                showAlert({ severity: 'success', text: 'Ordner wurde erstellt.' });
            } else if (selectedFolder) {
                await updateFolderMutation.mutateAsync({
                    folderId: selectedFolder.id,
                    payload: {
                        ...payloadForApi,
                        parent_id: selectedFolder.parent_id ?? null,
                        description: payload.description || undefined,
                    },
                });
                showAlert({ severity: 'success', text: 'Ordner wurde aktualisiert.' });
            }
            setFolderDialogOpen(false);
        } catch (error) {
            showAlert({ severity: 'error', text: `Ordner konnte nicht gespeichert werden: ${(error as Error).message}` });
        }
    };

    const handleDeleteFolder = async () => {
        if (!selectedFolder) {
            return;
        }
        try {
            const parentId = selectedFolder.parent_id ?? null;
            await deleteFolderMutation.mutateAsync({ folderId: selectedFolder.id });
            setDeleteDialogOpen(false);
            setSelectedFolderId(parentId);
            showAlert({ severity: 'success', text: 'Ordner wurde archiviert.' });
        } catch (error) {
            showAlert({ severity: 'error', text: `Ordner konnte nicht archiviert werden: ${(error as Error).message}` });
        }
    };

    const handleFileInputChange: React.ChangeEventHandler<HTMLInputElement> = async (event) => {
        if (!selectedFolder || !event.target.files || event.target.files.length === 0) {
            return;
        }

        const selectedFiles = Array.from(event.target.files);
        try {
            for (const file of selectedFiles) {
                await uploadFileMutation.mutateAsync({ folderId: selectedFolder.id, file });
            }
            showAlert({ severity: 'success', text: `${selectedFiles.length} Datei(en) hochgeladen.` });
        } catch (error) {
            showAlert({ severity: 'error', text: `Upload fehlgeschlagen: ${(error as Error).message}` });
        } finally {
            event.target.value = '';
        }
    };

    const handleDeleteFile = useCallback(async (fileId: number) => {
        if (!selectedFolder) {
            return;
        }
        try {
            await deleteFileMutation.mutateAsync({ folderId: selectedFolder.id, fileId });
            showAlert({ severity: 'success', text: 'Datei wurde entfernt.' });
        } catch (error) {
            showAlert({ severity: 'error', text: `Datei konnte nicht entfernt werden: ${(error as Error).message}` });
        }
    }, [deleteFileMutation, selectedFolder, showAlert]);

    const openDeleteFileDialog = (file: IFileItem) => {
        setFileDeleteCandidate(file);
    };

    const handleDownloadFile = useCallback(async (file: IFileItem) => {
        setDownloadingFileIds((prev) => [...prev, file.id]);
        try {
            const { blob, fileName } = await downloadStorageFile(file.id, file.original_name);
            const objectUrl = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = objectUrl;
            anchor.download = fileName;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            URL.revokeObjectURL(objectUrl);
        } catch (error) {
            const message = (error as Error).message;
            if (message.includes('401') || message.includes('403')) {
                showAlert({ severity: 'error', text: 'Download nicht erlaubt. Bitte erneut anmelden oder Rechte prüfen.' });
            } else {
                showAlert({ severity: 'error', text: `Download fehlgeschlagen: ${message}` });
            }
        } finally {
            setDownloadingFileIds((prev) => prev.filter((id) => id !== file.id));
        }
    }, [showAlert]);

    const confirmDeleteFile = async () => {
        if (!fileDeleteCandidate) {
            return;
        }
        await handleDeleteFile(fileDeleteCandidate.id);
        setFileDeleteCandidate(null);
    };

    const openRenameDialog = (file: IFileItem) => {
        setRenameCandidate(file);
        setRenameValue(file.original_name);
    };

    const openMobileFolderActionsMenu = (event: React.MouseEvent<HTMLElement>) => {
        setMobileFolderActionsAnchorEl(event.currentTarget);
    };

    const closeMobileFolderActionsMenu = () => {
        setMobileFolderActionsAnchorEl(null);
    };

    const openMobileFileActionsMenu = (event: React.MouseEvent<HTMLElement>, file: IFileItem) => {
        setMobileFileActionsAnchorEl(event.currentTarget);
        setMobileFileActionsTarget(file);
    };

    const closeMobileFileActionsMenu = () => {
        setMobileFileActionsAnchorEl(null);
        setMobileFileActionsTarget(null);
    };

    const submitRenameFile = async () => {
        if (!selectedFolder || !renameCandidate) {
            return;
        }
        try {
            await renameFileMutation.mutateAsync({
                folderId: selectedFolder.id,
                fileId: renameCandidate.id,
                original_name: renameValue.trim(),
            });
            showAlert({ severity: 'success', text: 'Datei wurde umbenannt.' });
            setRenameCandidate(null);
            setRenameValue('');
        } catch (error) {
            showAlert({ severity: 'error', text: `Datei konnte nicht umbenannt werden: ${(error as Error).message}` });
        }
    };

    const filteredFiles = useMemo(() => {
        const term = fileSearchTerm.trim().toLowerCase();
        return files.filter((file) => {
            if (showOnlyFavorites && !file.is_favorite) {
                return false;
            }
            if (!term) {
                return true;
            }
            return file.original_name.toLowerCase().includes(term)
                || file.mime_type.toLowerCase().includes(term)
                || file.uploaded_by.displayName.toLowerCase().includes(term);
        });
    }, [fileSearchTerm, files, showOnlyFavorites]);

    const canDeleteFile = useCallback((file: IFileItem): boolean => {
        if (typeof file.can_delete === 'boolean') {
            return file.can_delete || canManageSelectedFolder;
        }
        return canManageSelectedFolder || file.uploaded_by.id === currentUserId;
    }, [canManageSelectedFolder, currentUserId]);

    const canRenameFile = useCallback((file: IFileItem): boolean => {
        if (typeof file.can_rename === 'boolean') {
            return file.can_rename || canManageSelectedFolder;
        }
        return canManageSelectedFolder || file.uploaded_by.id === currentUserId;
    }, [canManageSelectedFolder, currentUserId]);

    const fileColumns = useMemo<GridColDef<IFileItem>[]>(() => {
        const columns: GridColDef<IFileItem>[] = [
            {
                field: 'original_name',
                headerName: 'Name',
                flex: 1.3,
                minWidth: 220,
            },
            {
                field: 'size',
                headerName: 'Größe',
                width: 120,
                valueGetter: (_, row) => formatBytes(row.size),
            },
            {
                field: 'created_at',
                headerName: 'Hochgeladen am',
                flex: 1,
                minWidth: 170,
                valueGetter: (_, row) => formatDateTime(row.created_at),
            },
            {
                field: 'uploaded_by',
                headerName: 'Hochgeladen von',
                flex: 1,
                minWidth: 170,
                valueGetter: (_, row) => row.uploaded_by.displayName,
            },
            {
                field: 'actions',
                headerName: 'Aktionen',
                width: 140,
                sortable: false,
                filterable: false,
                renderCell: (params) => (
                    <Stack direction="row" spacing={0.5} sx={{ py: 0.5 }}>
                        <Tooltip title="Download">
                            <IconButton
                                size="small"
                                aria-label="download"
                                disabled={downloadingFileIds.includes(params.row.id)}
                                onClick={() => {
                                    void handleDownloadFile(params.row);
                                }}
                            >
                                <DownloadIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={params.row.is_favorite ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'}>
                            <IconButton
                                size="small"
                                aria-label="favorite"
                                onClick={() => {
                                    void handleToggleFileFavorite(params.row, !Boolean(params.row.is_favorite));
                                }}
                            >
                                {params.row.is_favorite ? <StarRoundedIcon fontSize="small" sx={{ color: 'var(--pvta-favorite)' }} /> : <StarBorderRoundedIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                        {canRenameFile(params.row) && (
                            <Tooltip title="Umbenennen">
                                <IconButton
                                    size="small"
                                    aria-label="rename"
                                    onClick={() => {
                                        openRenameDialog(params.row);
                                    }}
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {canDeleteFile(params.row) && (
                            <Tooltip title="Entfernen">
                                <IconButton
                                    size="small"
                                    aria-label="remove"
                                    onClick={() => {
                                        openDeleteFileDialog(params.row);
                                    }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                ),
            },
        ];
        return columns;
    }, [canDeleteFile, canRenameFile, downloadingFileIds, handleDownloadFile, handleToggleFileFavorite]);

    const renderFolderNode = (node: IFolderTreeNode, depth = 0): React.ReactNode => {
        const isExpanded = expandedFolderIds.includes(node.id);
        const hasChildren = node.children.length > 0;

        return (
            <React.Fragment key={node.id}>
                <ListItemButton
                    selected={selectedFolderId === node.id}
                    sx={{
                        pl: 1.5 + depth * 1.5,
                        pr: 1,
                        py: 0.5,
                        minWidth: 0,
                        alignItems: 'flex-start',
                        borderRadius: 1,
                        mx: 0.5,
                        my: 0.25,
                        '&:hover': { transform: 'none' },
                    }}
                    onClick={() => handleSelectFolder(node.id)}
                    disabled={node.can_view === false}
                    >
                        <ListItemIcon sx={{ minWidth: 28, mt: 0.25 }}>
                        {node.is_favorite ? <StarRoundedIcon fontSize="small" sx={{ color: 'var(--pvta-favorite)' }} /> : <FolderIcon fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText
                        primary={node.name}
                        secondary={typeof node.file_count === 'number' ? `${node.file_count} Dateien` : undefined}
                        sx={{ minWidth: 0, mr: 0.5 }}
                        primaryTypographyProps={{
                            title: node.name,
                            sx: {
                                whiteSpace: 'normal',
                                overflowWrap: 'anywhere',
                                wordBreak: 'break-word',
                                lineHeight: 1.25,
                            },
                        }}
                        secondaryTypographyProps={{
                            variant: 'caption',
                            color: 'text.secondary',
                            sx: { display: 'block', mt: 0.25 },
                        }}
                    />
                    {hasChildren && (
                        <IconButton
                            size="small"
                            onClick={(event) => {
                                event.stopPropagation();
                                toggleExpand(node.id);
                            }}
                        >
                            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                    )}
                </ListItemButton>
                {hasChildren && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <List disablePadding>
                            {node.children.map((child) => renderFolderNode(child, depth + 1))}
                        </List>
                    </Collapse>
                )}
            </React.Fragment>
        );
    };

    return (
        <Box sx={{ py: { xs: 3, md: 4 }, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
            <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 3 } }}>
                <Stack spacing={3}>
                    <PageHeader
                        title="Datenablage"
                        subtitle="Dokumente und Dateien des Vereins."
                        action={(
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Chip
                                    label="Nur Favoriten"
                                    size="small"
                                    color={showOnlyFavorites ? 'primary' : 'default'}
                                    onClick={() => setShowOnlyFavorites((current) => !current)}
                                />
                                {canManageByGroupFallback && !isMobile && (
                                    <Button
                                        sx={{ ...primaryButtonSx }}
                                        startIcon={<AddIcon />}
                                        onClick={() => openCreateDialog(null)}
                                    >
                                        Neuer Hauptordner
                                    </Button>
                                )}
                            </Stack>
                        )}
                    />

                    {!isMobile && (
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4} lg={3}>
                                <Card sx={{ height: '100%' }}>
                                    <CardHeader title="Ordner" />
                                    <Divider />
                                    <CardContent sx={{ p: 1 }}>
                                        {isLoadingTree ? (
                                            <Stack spacing={1} sx={{ p: 1 }}>
                                                <Skeleton variant="rounded" height={36} />
                                                <Skeleton variant="rounded" height={36} />
                                                <Skeleton variant="rounded" height={36} />
                                            </Stack>
                                        ) : visibleTree.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                                Keine Unterordner.
                                            </Typography>
                                        ) : (
                                            <List disablePadding>
                                                {visibleTree.map((node) => renderFolderNode(node))}
                                            </List>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={8} lg={9}>
                                <Card>
                                    <CardHeader
                                        title={selectedFolder?.name ?? 'Ordner auswählen'}
                                        action={(
                                            <Stack direction="row" spacing={1}>
                                                {canManageSelectedFolder && (
                                                    <Button
                                                        size="small"
                                                        startIcon={<AddIcon />}
                                                        onClick={() => openCreateDialog(selectedFolder?.id ?? null)}
                                                    >
                                                        Neuer Unterordner
                                                    </Button>
                                                )}
                                                {selectedFolder && (
                                                    <Tooltip title={selectedFolder.is_favorite ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => { void handleToggleFolderFavorite(selectedFolder, !Boolean(selectedFolder.is_favorite)); }}
                                                        >
                                                            {selectedFolder.is_favorite ? <StarRoundedIcon sx={{ color: 'var(--pvta-favorite)' }} /> : <StarBorderRoundedIcon />}
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {selectedFolder && canManageSelectedFolder && (
                                                    <>
                                                        <Button size="small" startIcon={<EditIcon />} onClick={openEditDialog}>
                                                            Ordner bearbeiten
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            color="error"
                                                            startIcon={<DeleteIcon />}
                                                            onClick={() => setDeleteDialogOpen(true)}
                                                        >
                                                            Archivieren
                                                        </Button>
                                                    </>
                                                )}
                                                {selectedFolder && canUploadToSelectedFolder && (
                                                    <Button
                                                        size="small"
                                                        sx={{ ...primaryButtonSx }}
                                                        startIcon={<UploadFileIcon />}
                                                        onClick={() => fileInputRef.current?.click()}
                                                    >
                                                        Datei hochladen
                                                    </Button>
                                                )}
                                            </Stack>
                                        )}
                                    />
                                    <Divider />
                                    <CardContent>
                                        {!selectedFolder ? (
                                            <Typography variant="body2" color="text.secondary">
                                                Bitte links einen Ordner auswählen.
                                            </Typography>
                                        ) : (
                                            <Stack spacing={2.5}>
                                                <Breadcrumbs aria-label="breadcrumb">
                                                    {selectedPath.map((folder, index) => {
                                                        const isLast = index === selectedPath.length - 1;
                                                        return isLast ? (
                                                            <Typography key={folder.id} color="text.primary">
                                                                {folder.name}
                                                            </Typography>
                                                        ) : (
                                                            <Link
                                                                key={folder.id}
                                                                component="button"
                                                                type="button"
                                                                underline="hover"
                                                                color="inherit"
                                                                onClick={() => setSelectedFolderId(folder.id)}
                                                            >
                                                                {folder.name}
                                                            </Link>
                                                        );
                                                    })}
                                                </Breadcrumbs>

                                                <Stack
                                                    direction={{ xs: 'column', md: 'row' }}
                                                    spacing={1}
                                                    useFlexGap
                                                    flexWrap="wrap"
                                                >
                                                    <Chip
                                                        label={`Erstellt am ${formatDateTime(selectedFolder.created_at)} von ${selectedFolder.created_by.displayName}`}
                                                        size="small"
                                                    />
                                                    {selectedFolder.updated_at && selectedFolder.updated_by && (
                                                        <Chip
                                                            label={`Zuletzt geändert ${formatDateTime(selectedFolder.updated_at)} von ${selectedFolder.updated_by.displayName}`}
                                                            size="small"
                                                        />
                                                    )}
                                                    {(typeof selectedFolder.child_count === 'number'
                                                        || typeof selectedFolder.file_count === 'number') && (
                                                        <Chip
                                                            label={`Unterordner: ${selectedFolder.child_count ?? '-'} | Dateien: ${selectedFolder.file_count ?? '-'}`}
                                                            size="small"
                                                        />
                                                    )}
                                                </Stack>

                                                <Stack
                                                    direction={{ xs: 'column', sm: 'row' }}
                                                    spacing={1}
                                                    useFlexGap
                                                    flexWrap="wrap"
                                                >
                                                    <Chip
                                                        label={`Direkt eingestellt: ${describeAudience(directAudienceFromNode(selectedFolder), resolveGroupName)}`}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                    <Chip
                                                        label={`Effektiv sichtbar für: ${describeAudience(effectiveAudienceFromNode(selectedFolder), resolveGroupName)}`}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: STATUS_TONES.info.bg,
                                                            color: STATUS_TONES.info.color,
                                                        }}
                                                    />
                                                </Stack>

                                                {selectedFolder.restricted_by_ancestor && (
                                                    <Alert
                                                        severity="warning"
                                                        sx={{
                                                            borderRadius: 'var(--pvta-radius-sm)',
                                                            backgroundColor: STATUS_TONES.warning.bg,
                                                            color: STATUS_TONES.warning.color,
                                                        }}
                                                    >
                                                        Durch übergeordneten Ordner eingeschränkt: Die effektive Sichtbarkeit
                                                        ist enger als die direkte Einstellung dieses Ordners.
                                                    </Alert>
                                                )}

                                                <TextField
                                                    label="Dateien suchen"
                                                    value={fileSearchTerm}
                                                    onChange={(event) => setFileSearchTerm(event.target.value)}
                                                    size="small"
                                                    sx={fieldSx}
                                                />

                                                <Box sx={{ minHeight: 420 }}>
                                                    <DataGrid
                                                        rows={filteredFiles}
                                                        columns={fileColumns}
                                                        loading={isLoadingFiles}
                                                        disableRowSelectionOnClick
                                                        autoHeight
                                                        pageSizeOptions={[10, 25, 50]}
                                                        initialState={{
                                                            sorting: {
                                                                sortModel: [{ field: 'created_at', sort: 'desc' }],
                                                            },
                                                            pagination: {
                                                                paginationModel: { page: 0, pageSize: 10 },
                                                            },
                                                        }}
                                                        localeText={{
                                                            noRowsLabel: 'Keine Dateien in diesem Ordner',
                                                        }}
                                                    />
                                                </Box>
                                            </Stack>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    )}

                    {isMobile && mobilePane === 'folders' && (
                        <Card sx={{ width: '100%', maxWidth: '100%', mx: 'auto', overflowX: 'hidden' }}>
                            <CardHeader
                                title="Ordner"
                                titleTypographyProps={{ variant: 'subtitle1', sx: { fontWeight: 600 } }}
                                sx={{
                                    py: 1,
                                    px: 1,
                                    '& .MuiCardHeader-content': { minWidth: 0 },
                                    '& .MuiCardHeader-action': { ml: 1, flexShrink: 0, alignSelf: 'center', m: 0 },
                                }}
                                action={canManageByGroupFallback ? (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Chip
                                            label="Nur Favoriten"
                                            size="small"
                                            color={showOnlyFavorites ? 'primary' : 'default'}
                                            onClick={() => setShowOnlyFavorites((current) => !current)}
                                        />
                                        <Button size="small" variant="text" startIcon={<AddIcon />} onClick={() => openCreateDialog(null)}>
                                            Neuer Hauptordner
                                        </Button>
                                    </Stack>
                                ) : undefined}
                            />
                            <Divider />
                            <CardContent sx={{ p: 0.5 }}>
                                {isLoadingTree ? (
                                    <Stack spacing={1} sx={{ p: 1 }}>
                                        <Skeleton variant="rounded" height={36} />
                                        <Skeleton variant="rounded" height={36} />
                                        <Skeleton variant="rounded" height={36} />
                                    </Stack>
                                ) : visibleTree.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                        Keine Unterordner.
                                    </Typography>
                                ) : (
                                    <List disablePadding>
                                        {visibleTree.map((node) => renderFolderNode(node))}
                                    </List>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {isMobile && mobilePane === 'files' && (
                        <Card sx={{ width: '100%', maxWidth: '100%', mx: 'auto', overflowX: 'hidden' }}>
                            <Box sx={{ px: 2, pt: 2, pb: 1.5, minWidth: 0, maxWidth: '100%' }}>
                                <Stack spacing={1.25} sx={{ minWidth: 0 }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            minWidth: 0,
                                        }}
                                    >
                                        <Button
                                            size="small"
                                            startIcon={<ArrowBackIcon />}
                                            onClick={() => setMobilePane('folders')}
                                        >
                                            Zurück
                                        </Button>
                                        {hasMobileFolderActions && (
                                            <IconButton size="small" onClick={openMobileFolderActionsMenu} aria-label="ordner-aktionen">
                                                <MoreVertIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                    <Typography
                                        variant="h6"
                                        sx={{ minWidth: 0, whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                                    >
                                        {selectedFolder?.name ?? 'Ordner auswählen'}
                                    </Typography>
                                </Stack>
                            </Box>
                            <Divider />
                            <CardContent sx={{ px: 2, overflowX: 'hidden', minWidth: 0, maxWidth: '100%' }}>
                                {!selectedFolder ? (
                                    <Typography variant="body2" color="text.secondary">
                                        Bitte einen Ordner auswählen.
                                    </Typography>
                                ) : (
                                    <Stack spacing={2} sx={{ minWidth: 0, maxWidth: '100%' }}>
                                        <Breadcrumbs
                                            aria-label="breadcrumb"
                                            sx={{
                                                minWidth: 0,
                                                maxWidth: '100%',
                                                overflowX: 'hidden',
                                                '& .MuiBreadcrumbs-ol': { minWidth: 0, maxWidth: '100%', flexWrap: 'nowrap' },
                                                '& .MuiBreadcrumbs-li': { minWidth: 0, overflow: 'hidden' },
                                            }}
                                        >
                                            {selectedPath.map((folder, index) => {
                                                const isLast = index === selectedPath.length - 1;
                                                return isLast ? (
                                                    <Typography
                                                        key={folder.id}
                                                        color="text.primary"
                                                        title={folder.name}
                                                        sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                                                    >
                                                        {folder.name}
                                                    </Typography>
                                                ) : (
                                                    <Link
                                                        key={folder.id}
                                                        component="button"
                                                        type="button"
                                                        underline="hover"
                                                        color="inherit"
                                                        onClick={() => handleSelectFolder(folder.id)}
                                                        sx={{ minWidth: 0, whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                                                    >
                                                        {folder.name}
                                                    </Link>
                                                );
                                            })}
                                        </Breadcrumbs>

                                        <TextField
                                            label="Dateien suchen"
                                            value={fileSearchTerm}
                                            onChange={(event) => setFileSearchTerm(event.target.value)}
                                            size="small"
                                            fullWidth
                                            sx={fieldSx}
                                        />

                                        {isLoadingFiles ? (
                                            <Stack spacing={1.5}>
                                                <Skeleton variant="rounded" height={88} />
                                                <Skeleton variant="rounded" height={88} />
                                            </Stack>
                                        ) : filteredFiles.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">
                                                Keine Dateien in diesem Ordner.
                                            </Typography>
                                        ) : (
                                            <Stack spacing={1.5} sx={{ minWidth: 0, width: '100%', maxWidth: '100%' }}>
                                                {filteredFiles.map((file) => (
                                                        <Card
                                                            key={file.id}
                                                            variant="outlined"
                                                            sx={{ width: '100%', maxWidth: '100%', mx: 'auto', minWidth: 0, overflow: 'hidden' }}
                                                        >
                                                            <CardContent sx={{ py: 1.5, minWidth: 0, width: '100%', maxWidth: '100%' }}>
                                                                <Stack spacing={0.5} sx={{ minWidth: 0, width: '100%', maxWidth: '100%' }}>
                                                                    <Box sx={{ minWidth: 0, width: '100%', maxWidth: '100%', flex: '1 1 auto' }}>
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        sx={{
                                                                            fontWeight: 600,
                                                                            minWidth: 0,
                                                                            display: 'block',
                                                                            width: '100%',
                                                                            maxWidth: '100%',
                                                                            overflow: 'hidden',
                                                                            whiteSpace: 'normal !important',
                                                                            overflowWrap: 'anywhere !important',
                                                                            wordBreak: 'break-word !important',
                                                                            hyphens: 'auto',
                                                                        }}
                                                                        title={file.original_name}
                                                                    >
                                                                        {file.original_name}
                                                                    </Typography>
                                                                    </Box>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                        sx={{ minWidth: 0, display: 'block', width: '100%', maxWidth: '100%', wordBreak: 'break-word' }}
                                                                    >
                                                                        {formatBytes(file.size)} | {formatDateTime(file.created_at)}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                        sx={{ minWidth: 0, display: 'block', width: '100%', maxWidth: '100%', wordBreak: 'break-word' }}
                                                                    >
                                                                        Von {file.uploaded_by.displayName}
                                                                    </Typography>
                                                                    <Box
                                                                        sx={{
                                                                            mt: 0.5,
                                                                            minWidth: 0,
                                                                            width: '100%',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'space-between',
                                                                        }}
                                                                    >
                                                                        <Tooltip title="Download">
                                                                            <IconButton
                                                                                size="small"
                                                                                aria-label={`download-${file.id}`}
                                                                                disabled={downloadingFileIds.includes(file.id)}
                                                                                onClick={() => { void handleDownloadFile(file); }}
                                                                            >
                                                                                <DownloadIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        <Tooltip title={file.is_favorite ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'}>
                                                                            <IconButton
                                                                                size="small"
                                                                                aria-label={`favorite-${file.id}`}
                                                                                onClick={() => { void handleToggleFileFavorite(file, !Boolean(file.is_favorite)); }}
                                                                            >
                                                                                {file.is_favorite ? <StarRoundedIcon fontSize="small" sx={{ color: 'var(--pvta-favorite)' }} /> : <StarBorderRoundedIcon fontSize="small" />}
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        {(canRenameFile(file) || canDeleteFile(file)) && (
                                                                            <Tooltip title="Aktionen">
                                                                                <IconButton
                                                                                    size="small"
                                                                                    aria-label={`actions-${file.id}`}
                                                                                    onClick={(event) => openMobileFileActionsMenu(event, file)}
                                                                                >
                                                                                    <MoreVertIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        )}
                                                                    </Box>
                                                                </Stack>
                                                            </CardContent>
                                                        </Card>
                                                ))}
                                            </Stack>
                                        )}
                                    </Stack>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </Stack>
            </Container>

            <Menu
                anchorEl={mobileFolderActionsAnchorEl}
                open={Boolean(mobileFolderActionsAnchorEl)}
                onClose={closeMobileFolderActionsMenu}
            >
                {selectedFolder && canManageSelectedFolder && (
                    <MenuItem
                        onClick={() => {
                            closeMobileFolderActionsMenu();
                            openCreateDialog(selectedFolder.id);
                        }}
                    >
                        Neuer Unterordner
                    </MenuItem>
                )}
                {selectedFolder && canUploadToSelectedFolder && (
                    <MenuItem
                        onClick={() => {
                            closeMobileFolderActionsMenu();
                            fileInputRef.current?.click();
                        }}
                    >
                        Hochladen
                    </MenuItem>
                )}
                {selectedFolder && canManageSelectedFolder && (
                    <MenuItem
                        onClick={() => {
                            closeMobileFolderActionsMenu();
                            openEditDialog();
                        }}
                    >
                        Ordner bearbeiten
                    </MenuItem>
                )}
                {selectedFolder && canManageSelectedFolder && (
                    <MenuItem
                        onClick={() => {
                            closeMobileFolderActionsMenu();
                            setDeleteDialogOpen(true);
                        }}
                    >
                        Archivieren
                    </MenuItem>
                )}
            </Menu>

            <Menu
                anchorEl={mobileFileActionsAnchorEl}
                open={Boolean(mobileFileActionsAnchorEl)}
                onClose={closeMobileFileActionsMenu}
            >
                {mobileFileActionsTarget && canRenameFile(mobileFileActionsTarget) && (
                    <MenuItem
                        onClick={() => {
                            openRenameDialog(mobileFileActionsTarget);
                            closeMobileFileActionsMenu();
                        }}
                    >
                        Umbenennen
                    </MenuItem>
                )}
                {mobileFileActionsTarget && canDeleteFile(mobileFileActionsTarget) && (
                    <MenuItem
                        onClick={() => {
                            openDeleteFileDialog(mobileFileActionsTarget);
                            closeMobileFileActionsMenu();
                        }}
                    >
                        Entfernen
                    </MenuItem>
                )}
            </Menu>

            <input
                ref={fileInputRef}
                type="file"
                hidden
                multiple
                onChange={(event) => {
                    void handleFileInputChange(event);
                }}
            />

            <FolderDialog
                open={folderDialogOpen}
                mode={folderDialogMode}
                initialValues={getDialogInitialValues()}
                groupOptions={groups}
                parentEffective={dialogParentEffective}
                onClose={() => setFolderDialogOpen(false)}
                onSubmit={handleFolderDialogSubmit}
            />

            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Ordner archivieren?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Soll der Ordner &quot;{selectedFolder?.name}&quot; wirklich archiviert werden?
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Hinweis: Der Eintrag wird archiviert/entfernt.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button sx={secondaryButtonSx} onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
                    <Button sx={dangerButtonSx} onClick={() => { void handleDeleteFolder(); }}>
                        Archivieren
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={fileDeleteCandidate !== null} onClose={() => setFileDeleteCandidate(null)}>
                <DialogTitle>Datei entfernen?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Soll die Datei &quot;{fileDeleteCandidate?.original_name}&quot; wirklich entfernt werden?
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Hinweis: Der Eintrag wird archiviert/entfernt.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button sx={secondaryButtonSx} onClick={() => setFileDeleteCandidate(null)}>Abbrechen</Button>
                    <Button sx={dangerButtonSx} onClick={() => { void confirmDeleteFile(); }}>
                        Entfernen
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={renameCandidate !== null} onClose={() => setRenameCandidate(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Datei umbenennen</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <TextField
                        fullWidth
                        label="Neuer Dateiname"
                        value={renameValue}
                        onChange={(event) => setRenameValue(event.target.value)}
                        sx={fieldSx}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button sx={secondaryButtonSx} onClick={() => setRenameCandidate(null)}>Abbrechen</Button>
                    <Button
                        sx={{ ...primaryButtonSx }}
                        onClick={() => { void submitRenameFile(); }}
                        disabled={!renameValue.trim()}
                    >
                        Speichern
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default StoragePage;
